import React, { FC, useMemo } from 'react';
import Map, { LeafletMapPolyline, MapMarker } from '@thingslog/ui-components/src/Map/Map';
import {
  GnssDeviceReadingResponseDto,
  GnssReadingResponseDto,
} from '@thingslog/repositories/src/deviceCounter/DeviceReadingsResponseDto';
import L from 'leaflet';
import { InfoDisplay } from '../InfoDisplay';
import { ShowChart } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

interface SensorsDataMapProps {
  data: GnssDeviceReadingResponseDto[];
  isLoading?: boolean;
}

const CHART_COLORS = [
  '#2196F3',
  '#43A047',
  '#E53935',
  '#FF8C00',
  '#AB47BC',
  '#F2C94C',
  '#2ACFCF',
  '#FF69B4',
  '#795548',
  '#546E7A',
];

interface MapData {
  polylines: LeafletMapPolyline[];
  markers: MapMarker[];
  center: [number, number];
}

export const SensorsDataMap: FC<SensorsDataMapProps> = ({
  data,
  isLoading = false,
}: SensorsDataMapProps) => {
  const { t } = useTranslation();
  const { polylines, markers, center }: MapData = useMemo(() => {
    const polylinesList: LeafletMapPolyline[] = [];
    const markersList: MapMarker[] = [];
    const mapCenter: [number, number] = [42.6977, 23.3219];

    data.forEach((item: GnssDeviceReadingResponseDto, index: number) => {
      const { readings } = item;
      if (readings.length > 0) {
        const polyline: LeafletMapPolyline = {
          positions: readings.map((reading: GnssReadingResponseDto) =>
            L.latLng(reading.latitude, reading.longitude)
          ),

          color: CHART_COLORS[index % CHART_COLORS.length],
        };
        polylinesList.push(polyline);
      }
    });

    return { polylines: polylinesList, markers: markersList, center: mapCenter };
  }, [data]);

  if (isLoading) {
    // You might want a map-specific loading placeholder
    return (
      <div className="h-full flex items-center justify-center">{t('sensors_data_map_loading')}</div>
    );
  }

  if (data.length === 0 || polylines.length === 0) {
    return (
      <InfoDisplay
        icon={<ShowChart />}
        title={t('sensors_data_chart_no_data_available')}
        subtitle={t('sensors_data_chart_no_numeric_data_subtitle')}
      />
    );
  }

  return (
    <div className="h-full w-full min-h-[400px] flex flex-col">
      <Map
        className="flex-1"
        center={center}
        zoom={13}
        markers={markers}
        polylines={polylines}
        scrollWheelZoom={true}
      />
    </div>
  );
};
